import React, { useState, useEffect } from 'react'
import { dbOperations } from '../../utils/supabase'
import {
  ShoppingBagIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  TagIcon,
  CubeIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalUsers: 0,
    totalCategories: 0,
    totalProductSets: 0,
    recentOrders: [],
    lowStockProducts: []
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      console.log('AdminDashboard: Starting data load...')
      
      // Önce basit veriler
      const productsResponse = await dbOperations.products.getAll()
      const usersResponse = await dbOperations.users.getAll()
      const categoriesResponse = await dbOperations.categories.getAll()
      const productSetsResponse = await dbOperations.productSets.getAll()
      
      // Son olarak orders (foreign key problemi olabilir)
      let ordersResponse = { data: [], error: null }
      try {
        ordersResponse = await dbOperations.orders.getAll()
      } catch (error) {
        console.error('Orders fetch failed, using empty array:', error)
        ordersResponse = { data: [], error: null }
      }

      console.log('AdminDashboard: Responses received:', {
        productsResponse,
        ordersResponse,
        usersResponse,
        categoriesResponse,
        productSetsResponse
      })

      const products = productsResponse.data || []
      const orders = ordersResponse.data || []
      const users = usersResponse.data || []
      const categories = categoriesResponse.data || []
      const productSets = productSetsResponse.data || []

      // Son 5 siparişi al
      const recentOrders = orders.slice(0, 5)

      // Stoku az olan ürünleri bul (stok < 10)
      const lowStockProducts = products.filter(product => product.stock < 10)

      setStats({
        totalProducts: products.length,
        totalOrders: orders.length,
        totalUsers: users.length,
        totalCategories: categories.length,
        totalProductSets: productSets.length,
        recentOrders,
        lowStockProducts
      })
    } catch (error) {
      console.error('Dashboard data loading error:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'processing':
        return 'bg-blue-100 text-blue-800'
      case 'shipped':
        return 'bg-purple-100 text-purple-800'
      case 'delivered':
        return 'bg-green-100 text-green-800'
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'Bekliyor'
      case 'processing':
        return 'İşleniyor'
      case 'shipped':
        return 'Kargoda'
      case 'delivered':
        return 'Teslim Edildi'
      case 'cancelled':
        return 'İptal Edildi'
      default:
        return status
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  const statCards = [
    {
      name: 'Toplam Ürün',
      value: stats.totalProducts,
      icon: ShoppingBagIcon,
      color: 'bg-blue-500',
      href: '/admin/products'
    },
    {
      name: 'Ürün Takımları',
      value: stats.totalProductSets,
      icon: CubeIcon,
      color: 'bg-indigo-500',
      href: '/admin/product-sets'
    },
    {
      name: 'Toplam Sipariş',
      value: stats.totalOrders,
      icon: ClipboardDocumentListIcon,
      color: 'bg-green-500',
      href: '/admin/orders'
    },
    {
      name: 'Toplam Kullanıcı',
      value: stats.totalUsers,
      icon: UsersIcon,
      color: 'bg-purple-500',
      href: '/admin/users'
    },
    {
      name: 'Toplam Kategori',
      value: stats.totalCategories,
      icon: TagIcon,
      color: 'bg-orange-500',
      href: '/admin/categories'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Mobilya e-ticaret yönetim paneline hoş geldiniz
        </p>
      </div>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
          >
            <dt>
              <div className={`absolute rounded-md p-3 ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <p className="ml-16 text-sm font-medium text-gray-500 truncate">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
            </dd>
            <div className="absolute bottom-0 inset-x-0 bg-gray-50 px-4 py-4 sm:px-6">
              <div className="text-sm">
                <a
                  href={stat.href}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Görüntüle
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Son Siparişler */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Son Siparişler
            </h3>
            {stats.recentOrders.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Henüz sipariş yok</p>
            ) : (
              <div className="space-y-3">
                {stats.recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {order.user_profiles?.full_name || order.user_profiles?.email || 'Bilinmeyen Kullanıcı'}
                        </p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(order.total_amount)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <a
                href="/admin/orders"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Tüm siparişleri görüntüle →
              </a>
            </div>
          </div>
        </div>

        {/* Stoku Az Ürünler */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Stoku Az Ürünler
            </h3>
            {stats.lowStockProducts.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Stoku az ürün yok</p>
            ) : (
              <div className="space-y-3">
                {stats.lowStockProducts.slice(0, 5).map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {product.name}
                        </p>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                          {product.stock} adet
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-500">
                          {product.category} - {formatCurrency(product.price)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4">
              <a
                href="/admin/products"
                className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
              >
                Tüm ürünleri görüntüle →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard 